/**
 * @fileoverview Group Management Hooks
 *
 * Custom React hooks for managing group operations through the service layer.
 * Provides a clean API for components to perform group CRUD operations, workspace
 * associations, and item reordering with consistent error handling and state updates.
 *
 * Features:
 * - CRUD operations for groups (create, read, update, delete)
 * - Workspace association management
 * - Item reordering within groups
 * - Automatic state refresh after operations
 * - Consistent error handling and user feedback
 * - Integration with Redux workspace state for data synchronization
 *
 * Usage:
 *   const { deleteGroup, createGroup, updateGroup } = useGroupHooks();
 *   await createGroup({ name: 'My Group', type: 'links' });
 */

import { useCallback } from "react";
import { groupService } from "../services/index";
import { useWorkspace } from "@workspace/hooks/useReduxWorkspace";
import { useBanner } from "./useBanner";
import { useUser } from "./useAuth";
import { errorMessage } from "@jkast90/kast-common/format";
import { withErrorHandling } from "../utils/errorUtils";
import type {
  CreateGroupData,
  UpdateGroupData,
} from "../services/groupService";
import { logger } from "../utils/logger";

/**
 * Hook providing group management operations.
 *
 * Integrates with the service layer and workspace context to provide
 * a complete group management API with error handling and state updates.
 *
 * @returns {Object} Group operation functions
 * @returns {Function} deleteGroup - Delete a group by ID
 * @returns {Function} createGroup - Create a new group
 * @returns {Function} updateGroup - Update an existing group
 * @returns {Function} linkGroupToWorkspace - Associate group with workspace
 * @returns {Function} unlinkGroupFromWorkspace - Remove group from workspace
 * @returns {Function} reorderGroupItems - Reorder items within a group
 */
export function useGroupHooks() {
  const { loadAllWorkspaces } = useWorkspace();
  const { showBanner } = useBanner();
  const { userEmail } = useUser();

  // Refetch workspaces (which carry groups inline) after mutations.
  const refreshAfterMutation = useCallback(
    () => loadAllWorkspaces({ forceRefresh: true }),
    [loadAllWorkspaces],
  );

  /**
   * Delete a group and refresh workspace data.
   *
   * @param {number} groupId - ID of the group to delete
   * @param {string} groupName - Name of the group for user feedback
   */
  const deleteGroup = useCallback(
    async (groupId: number, groupName: string) => {
      logger.debug("Deleting group:", { groupId, groupName });

      try {
        await groupService.deleteGroup(groupId);
        showBanner(`Group "${groupName}" deleted`);
        await refreshAfterMutation();
        return true;
      } catch (error) {
        // Check for specific error types and build error message
        const message = errorMessage(error);
        let bannerMessage: string;
        if (
          message?.includes("Cannot delete group") &&
          message?.includes("link(s)")
        ) {
          bannerMessage = `Cannot delete "${groupName}": Please remove all links first`;
        } else if (
          message?.includes("Cannot delete group") &&
          message?.includes("note(s)")
        ) {
          bannerMessage = `Cannot delete "${groupName}": Please remove all notes first`;
        } else if (message?.includes("associated with a workspace")) {
          bannerMessage = `Cannot delete "${groupName}": Please remove from all workspaces first`;
        } else if (
          message?.includes("400") &&
          message?.includes("constraint")
        ) {
          bannerMessage = `Cannot delete "${groupName}": It still contains items or is linked to workspaces`;
        } else {
          bannerMessage = `Failed to delete group: ${message}`;
        }

        showBanner(bannerMessage);
        logger.error("Group deletion failed:", error);

        // Re-throw the error so bulk operations can properly handle it
        throw new Error(bannerMessage, { cause: error });
      }
    },
    [refreshAfterMutation, showBanner],
  );

  /**
   * Create a new group and refresh workspace data.
   *
   * @param {Object} groupData - Group configuration object
   * @param {string} groupData.name - Group name
   * @param {string} groupData.type - Group type ('links' or 'notes')
   * @returns {Promise<Object>} Created group object
   */
  const createGroup = useCallback(
    async (groupData: CreateGroupData) => {
      const operation = withErrorHandling(
        async () => {
          const newGroup = await groupService.createGroup(groupData, userEmail);
          await refreshAfterMutation();
          return newGroup;
        },
        showBanner,
        "Group creation",
        "Group created successfully",
      );

      return await operation();
    },
    [userEmail, refreshAfterMutation, showBanner],
  );

  /**
   * Update an existing group and refresh workspace data.
   *
   * @param {number} groupId - ID of the group to update
   * @param {Object} groupData - Updated group data
   * @returns {Promise<Object>} Updated group object
   */
  const updateGroup = useCallback(
    async (groupId: number, groupData: UpdateGroupData) => {
      const operation = withErrorHandling(
        async () => {
          const updatedGroup = await groupService.updateGroup(
            groupId,
            groupData,
          );
          await refreshAfterMutation();
          return updatedGroup;
        },
        showBanner,
        "Group update",
        "Group updated successfully",
      );

      return await operation();
    },
    [refreshAfterMutation, showBanner],
  );

  /**
   * Associate a group with a workspace.
   *
   * @param {number} workspaceId - ID of the target workspace
   * @param {number} groupId - ID of the group to link
   */
  const linkGroupToWorkspace = useCallback(
    async (workspaceId: number, groupId: number) => {
      if (!groupId || !workspaceId) return;

      const operation = withErrorHandling(
        async () => {
          await groupService.linkGroupToWorkspace(workspaceId, groupId);
          await refreshAfterMutation();
        },
        showBanner,
        "Linking group to workspace",
      );

      await operation();
    },
    [refreshAfterMutation, showBanner],
  );

  /**
   * Remove a group from a workspace.
   *
   * @param {number} workspaceId - ID of the workspace
   * @param {number} groupId - ID of the group to unlink
   */
  const unlinkGroupFromWorkspace = useCallback(
    async (workspaceId: number, groupId: number) => {
      const operation = withErrorHandling(
        async () => {
          await groupService.unlinkGroupFromWorkspace(workspaceId, groupId);
          await refreshAfterMutation();
        },
        showBanner,
        "Unlinking group from workspace",
      );

      await operation();
    },
    [refreshAfterMutation, showBanner],
  );

  /**
   * Reorder items within a group.
   *
   * @param {number} groupId - ID of the group containing items
   * @param {Array<number>} itemOrder - Array of item IDs in new order
   * @returns {Promise<Object>} Updated group object
   */
  const reorderGroupItems = useCallback(
    async (groupId: number, itemOrder: number[]) => {
      const operation = withErrorHandling(
        async () => {
          const reorderedGroup = await groupService.reorderGroupItems(
            groupId,
            itemOrder,
          );
          await refreshAfterMutation();
          return reorderedGroup;
        },
        showBanner,
        "Reordering group items",
      );

      return await operation();
    },
    [refreshAfterMutation, showBanner],
  );

  return {
    deleteGroup,
    createGroup,
    updateGroup,
    linkGroupToWorkspace,
    unlinkGroupFromWorkspace,
    reorderGroupItems,
  };
}
